/**
 * useBillPaymentBreakdown — fetch paid/refund breakdowns for a list of bills
 *
 * Calls GET /payments/v2/bills-payment-breakdown in a single batched request
 * whenever the supplied bill_ids array changes. Returns a map keyed by
 * bill_id with:
 *   { paid_breakdown, paid_count, paid_total,
 *     refund_breakdown, refund_count, refund_total }
 *
 * Designed for list pages (PF Bills, All Bills) — handles deduping and
 * cancellation. Limit per call: 200 bills (matches backend).
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';

const EMPTY = { paid_breakdown: {}, paid_count: 0, paid_total: 0,
                refund_breakdown: {}, refund_count: 0, refund_total: 0 };

export function useBillPaymentBreakdown(billIds, accountId) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  // Dedupe + filter empty
  const ids = Array.isArray(billIds)
    ? [...new Set(billIds.filter(Boolean))]
    : [];
  const idsKey = ids.slice(0, 200).join(',');

  useEffect(() => {
    if (!accountId || !idsKey) {
      setMap({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get('/payments/v2/bills-payment-breakdown', {
      params: { account_id: accountId, bill_ids: idsKey },
    })
      .then(({ data }) => {
        if (!cancelled) setMap(data?.results || {});
      })
      .catch(() => { if (!cancelled) setMap({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [idsKey, accountId]);

  const get = (billId) => map[billId] || EMPTY;
  return { get, map, loading };
}

export default useBillPaymentBreakdown;
